/**
 * Global "moved by coach" store — tracks task IDs that were just moved by
 * the coach so that Row components can render a temporary `↻` badge.
 *
 * Usage:
 *   // Producer (dopo applyOperations su spostamenti):
 *   import { flashMovedIds } from '../lib/movedFlash';
 *   flashMovedIds(['az-123', 'mit-456']);
 *
 *   // Consumer (dentro Row / MitRow / AzioneRow):
 *   import { useIsMoved } from '../lib/movedFlash';
 *   const isMoved = useIsMoved(id);
 *   {isMoved && <MovedBadge />}
 *
 * TTL: 3 secondi. Dopo, l'id viene rimosso automaticamente e le viste
 * sottoscritte si re-render senza badge.
 *
 * Implementazione: `useSyncExternalStore` per aggancio a React senza wrapper
 * context (pattern moderno e più leggero di Zustand per questo scope).
 */
import { useSyncExternalStore } from 'react';

// ─── Channel 1: MOVED (3s) — usato per il badge `↻ spostata` sulle Row ─────
const MOVED_TTL_MS = 3000;
const movedIds = new Set();
const movedTimers = new Map();
const movedListeners = new Set();

function emitMoved() { for (const l of movedListeners) l(); }

/** Aggiunge ids al channel "moved" con TTL 3s. Estende il TTL se già presente. */
export function flashMovedIds(newIds) {
  if (!Array.isArray(newIds) || newIds.length === 0) return;
  for (const id of newIds) {
    if (!id) continue;
    movedIds.add(id);
    const prev = movedTimers.get(id);
    if (prev) clearTimeout(prev);
    const t = setTimeout(() => {
      movedIds.delete(id);
      movedTimers.delete(id);
      emitMoved();
    }, MOVED_TTL_MS);
    movedTimers.set(id, t);
  }
  emitMoved();
}

function subscribeMoved(cb) { movedListeners.add(cb); return () => movedListeners.delete(cb); }
function getMovedSnapshot() { return movedIds; }

/** Hook: true se `id` è attualmente flaggato come "just moved" (TTL 3s). */
export function useIsMoved(id) {
  return useSyncExternalStore(subscribeMoved, () => movedIds.has(id), () => false);
}


// ─── Channel 2: COACH TOUCH (10s) — evidenziazione forte in CoachStatePanel ─
// L'utente vuole vedere quale KR/Azione/MIT è stato appena modificato dal coach
// nella colonna "Stato della settimana". Highlight forte per 10s, poi il subtle
// permanente (già gestito da `highlightIds` classico) rimane.
const TOUCH_TTL_MS = 10000;
const touchedIds = new Set();
const touchTimers = new Map();
const touchListeners = new Set();

function emitTouched() { for (const l of touchListeners) l(); }

/** Aggiunge ids al channel "coach touched" con TTL 10s. */
export function flashCoachTouched(newIds) {
  if (!Array.isArray(newIds) || newIds.length === 0) return;
  for (const id of newIds) {
    if (!id) continue;
    touchedIds.add(id);
    const prev = touchTimers.get(id);
    if (prev) clearTimeout(prev);
    const t = setTimeout(() => {
      touchedIds.delete(id);
      touchTimers.delete(id);
      emitTouched();
    }, TOUCH_TTL_MS);
    touchTimers.set(id, t);
  }
  emitTouched();
}

function subscribeTouched(cb) { touchListeners.add(cb); return () => touchListeners.delete(cb); }

/** Hook: true se `id` è attualmente "just touched by coach" (TTL 10s). */
export function useIsCoachTouched(id) {
  return useSyncExternalStore(subscribeTouched, () => touchedIds.has(id), () => false);
}


// ─── Test-only helpers ─────────────────────────────────────────────────────
export function _resetMovedFlash() {
  for (const t of movedTimers.values()) clearTimeout(t);
  movedTimers.clear();
  movedIds.clear();
  emitMoved();
  for (const t of touchTimers.values()) clearTimeout(t);
  touchTimers.clear();
  touchedIds.clear();
  emitTouched();
}

export function _probeHas(id) { return movedIds.has(id); }
export function _probeTouched(id) { return touchedIds.has(id); }
